import { Extension, type Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';

/** The smallest shape both triggers share: something with an id and something to show for it. */
export interface SuggestionItem {
  id: string;
  label: string;
}

/** What the composer renders while a trigger is open. Null means no menu. */
export interface SuggestionState<T extends SuggestionItem> {
  items: T[];
  query: string;
  /** Applies the user's choice to the document, replacing the typed trigger and query. */
  choose: (item: T) => void;
}

interface SuggestionConfig<T extends SuggestionItem> {
  /** Plugin name, and the key that keeps two triggers from sharing state. */
  name: string;
  /** The character that opens the menu. */
  char: string;
  /** Only open at the start of a line. The skill trigger uses this; the panel trigger does not. */
  startOfLine?: boolean;
  /** Panel labels contain spaces, so their query must be allowed to as well. */
  allowSpaces?: boolean;
  /** Candidates for a query. Returning none closes the menu, which is what frees the Enter key. */
  items: (query: string) => T[];
  /** Called on every open, update, and close. */
  onStateChange: (state: SuggestionState<T> | null) => void;
  /** Writes the choice into the document. The range covers the trigger and everything typed after it. */
  apply: (args: { editor: Editor; range: Range; item: T }) => void;
}

/**
 * Bridges a TipTap suggestion plugin to React state, so the menu stays an ordinary component.
 *
 * The plugin decides when a trigger is open, what the query is, and which range a choice replaces.
 * React decides what the menu looks like. Keyboard navigation stays in `MentionMenu`, which already
 * owns it — this extension only claims the keys so ProseMirror does not act on them first. When the
 * candidate list is empty the keys are released, and Enter sends the message as usual.
 */
export function createSuggestionExtension<T extends SuggestionItem>(config: SuggestionConfig<T>): Extension {
  // The key handler runs inside ProseMirror, which has no access to React state. This holder carries
  // the current candidate count across that boundary.
  const open = { hasItems: false };

  return Extension.create({
    name: config.name,

    addProseMirrorPlugins() {
      return [
        Suggestion<T>({
          editor: this.editor,
          pluginKey: new PluginKey(config.name),
          char: config.char,
          startOfLine: config.startOfLine ?? false,
          allowSpaces: config.allowSpaces ?? false,
          items: ({ query }) => config.items(query),
          command: ({ editor, range, props }) => {
            // `@tiptap/suggestion` types the chosen item as `any`, because it cannot know what the
            // `items` callback returns. It returns `T`, so narrow it here rather than downstream.
            config.apply({ editor, range, item: props as T });
          },
          render: () => ({
            onStart: (props) => {
              open.hasItems = props.items.length > 0;
              config.onStateChange({ items: props.items, query: props.query, choose: props.command });
            },
            onUpdate: (props) => {
              open.hasItems = props.items.length > 0;
              config.onStateChange({ items: props.items, query: props.query, choose: props.command });
            },
            onKeyDown: ({ event }) => {
              // With no candidates the menu is closed, so Enter must reach the composer and send.
              if (!open.hasItems) return false;
              return ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.key);
            },
            onExit: () => {
              open.hasItems = false;
              config.onStateChange(null);
            },
          }),
        }),
      ];
    },
  });
}
